import React, { useEffect, useState } from 'react';
import { Box, Button, Image, Text, VStack, Badge, Skeleton } from '@chakra-ui/react';
import type { VodItem } from '../types';
import { streamOutlineOnDarkHover } from '../darkThemeButtonProps';

interface Props {
  items: VodItem[];
  onSelect: (v: VodItem) => void;
  emptyHint?: string;
  dense?: boolean;
}

const PAGE = 150;

export function PosterGrid({ items, onSelect, emptyHint, dense }: Props) {
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    setVisible(PAGE);
  }, [items]);

  if (!items.length) {
    return (
      <Text fontSize="sm" color="whiteAlpha.500">
        {emptyHint ?? 'No titles in this category.'}
      </Text>
    );
  }

  const slice = items.slice(0, visible);
  const remaining = items.length - slice.length;

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns={{
          base: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: dense ? 'repeat(5, 1fr)' : 'repeat(5, 1fr)',
          xl: dense ? 'repeat(6, 1fr)' : undefined,
        }}
        gap={dense ? 2 : 4}
      >
        {slice.map((v) => (
        <Box
          key={v.id}
          role="group"
          cursor="pointer"
          onClick={() => onSelect(v)}
          borderRadius={dense ? 'lg' : 'xl'}
          overflow="hidden"
          bg="whiteAlpha.50"
          borderWidth="1px"
          borderColor="whiteAlpha.100"
          transition="all 0.15s"
          _hover={{ borderColor: 'teal.400', transform: 'translateY(-3px)' }}
        >
          <Box aspectRatio="2/3" bg="whiteAlpha.100" position="relative">
            {v.poster ? (
              <Image
                src={v.poster}
                alt=""
                w="100%"
                h="100%"
                objectFit="cover"
                fallback={<Skeleton w="100%" h="100%" />}
              />
            ) : (
              <VStack h="100%" justify="center" px={2}>
                <Text fontSize="xs" fontWeight="700" color="teal.200" textAlign="center" noOfLines={3}>
                  {v.name}
                </Text>
              </VStack>
            )}
            <Badge position="absolute" top={2} left={2} colorScheme="purple" variant="solid" fontSize="10px">
              VOD
            </Badge>
          </Box>
          <VStack align="stretch" p={dense ? 2 : 3} spacing={0.5}>
            <Text fontSize={dense ? 'xs' : 'sm'} fontWeight="600" color="white" noOfLines={2}>
              {v.name}
            </Text>
            <Text fontSize={dense ? '10px' : 'xs'} color="whiteAlpha.500">
              {[v.year, v.genre].filter(Boolean).join(' · ')}
            </Text>
          </VStack>
        </Box>
      ))}
      </Box>
      {remaining > 0 && (
        <Button
          mt={4}
          size="sm"
          variant="outline"
          borderColor="whiteAlpha.300"
          color="white"
          {...streamOutlineOnDarkHover}
          onClick={() => setVisible((n) => n + PAGE)}
        >
          Load more ({remaining.toLocaleString()} left)
        </Button>
      )}
    </Box>
  );
}
